import { isA1App } from '@shared/utils/platform/platformDetection';
import { LSK } from '@shared/utils/storage/config';
import { CacheDeps } from '@shared/utils/decorator';

import { originalSetDeviceId, originalSetGooglePlayReferrer } from './handle';

/** 游客模式 - 该模块无依赖问题 */
export default class Tourist {
	// 状态管理
	private static isAvailableFlag: boolean = false;
	private static deviceIdInfo: string = '';
	private static isInitialized: boolean = false;
	private static retryLoginCount: number = 3;
	private static isLoginSuccess: boolean = false;

	/**
	 * 游客模式是否可用的前提，具体判断当前游客模式是否可用得看deviceId是否为空
	 */
	@CacheDeps(['deviceIdInfo', 'isAvailableFlag'])
	static get isAvailable(): boolean {
		if (!isA1App) {
			return false;
		}
		// 如果未初始化，进行同步检查（仅从 localStorage 读取）
		if (!this.isInitialized) {
			this.initializeFromStorage();
		}
		return this.isAvailableFlag;
	}

	/**
	 * 设备ID
	 */
	@CacheDeps(['deviceIdInfo', 'isAvailableFlag'])
	static get deviceId(): string {
		if (!isA1App) {
			return '';
		}
		// 如果未初始化，进行同步检查（仅从 localStorage 读取）
		if (!this.isInitialized) {
			this.initializeFromStorage();
		}
		return this.deviceIdInfo;
	}

	/**
	 * 是否可以尝试登录
	 */
	static get canTryLogin(): boolean {
		return this.retryLoginCount > 0 && !this.isLoginSuccess;
	}

	/**
	 * 从 localStorage 初始化状态（同步方法，不调用原生接口）
	 */
	private static initializeFromStorage(): void {
		if (this.isInitialized) return;

		try {
			const deviceId = localStorage.getItem(LSK.DEVICE_ID);

			// 版本支持，但可能还没有 deviceId（需要异步获取）
			this.setAvailabilityState(true, deviceId || '');
		} catch (error) {
			console.error('初始化游客模式状态时出错:', error);
			this.setAvailabilityState(false, '');
		}
	}

	/**
	 * 设置可用性状态
	 */
	private static setAvailabilityState(isAvailable: boolean, deviceId: string): void {
		this.isAvailableFlag = isAvailable;
		this.deviceIdInfo = deviceId;
		this.isInitialized = true;
	}

	/**
	 * 异步检查游客模式可用性（会调用原生接口获取 deviceId）
	 */
	public static async asyncCheckAvailability(): Promise<{
		isAvailable: boolean;
		deviceId: string;
	}> {
		// 先进行同步初始化
		if (!this.isInitialized) {
			this.initializeFromStorage();
		}

		// 如果版本不支持，直接返回
		if (!this.isAvailableFlag) {
			return { isAvailable: false, deviceId: '' };
		}

		// 如果已有 deviceId，直接返回
		if (this.deviceIdInfo) {
			return { isAvailable: true, deviceId: this.deviceIdInfo };
		}

		// 尝试从原生接口获取 deviceId
		try {
			const deviceId = await this.fetchDeviceIdFromNative();
			this.setAvailabilityState(!!deviceId, deviceId || '');
			return {
				isAvailable: this.isAvailableFlag,
				deviceId: this.deviceIdInfo,
			};
		} catch (error) {
			console.error('异步获取设备ID失败:', error);
			this.setAvailabilityState(false, '');
			return { isAvailable: false, deviceId: '' };
		}
	}

	/**
	 * 从原生接口获取设备ID
	 */
	private static fetchDeviceIdFromNative(): Promise<string> {
		return new Promise<string>((resolve) => {
			// 兜底逻辑，10s后超时，则调用兜底逻辑
			window.setDeviceId = (deviceId: string) => {
				if (deviceId) {
					localStorage.setItem(LSK.DEVICE_ID, deviceId);
				}
				window.setDeviceId = originalSetDeviceId;
				clearTimeout(window.deviceIdTimeout);
				resolve(deviceId);
			};
			try {
				window.AndroidInterface?.getDeviceId?.('setDeviceId', 'setDeviceIdFailed');
			} catch (error) {
				console.error('获取设备ID失败:', error);
				window.AndroidInterface?.getDeviceId?.('setDeviceId');
			}
		});
	}

	/**
	 * 异步获取 Google Play Referrer
	 */
	public static async asyncGetGooglePlayReferrer(): Promise<string> {
		if (!window.AndroidInterface?.getGooglePlayReferrer) {
			return '';
		}

		return new Promise<string>((resolve) => {
			window.asyncSetGooglePlayReferrer = (googlePlayReferrer: string) => {
				originalSetGooglePlayReferrer(googlePlayReferrer);
				resolve(googlePlayReferrer);
			};
			window.AndroidInterface?.getGooglePlayReferrer?.('asyncSetGooglePlayReferrer');
		});
	}

	// ========== 公共设置方法 ==========

	/**
	 * 设置设备ID
	 */
	public static setDeviceId(deviceId: string): void {
		this.deviceIdInfo = deviceId;
		if (deviceId) {
			this.isAvailableFlag = true;
		}
	}

	/**
	 * 设置可用性标志
	 */
	public static setAvailableFlag(isAvailable: boolean): void {
		this.isAvailableFlag = isAvailable;
		if (!isAvailable) {
			this.deviceIdInfo = '';
		}
	}

	/**
	 * 减少重试登录次数
	 */
	public static setRetryLogin(): void {
		this.retryLoginCount = Math.max(0, this.retryLoginCount - 1);
	}

	/**
	 * 设置登录成功状态
	 */
	public static setLoginSuccess(isSuccess: boolean): void {
		this.isLoginSuccess = isSuccess;
	}
}
